import { useState, useEffect } from "react";
import { Button } from "./ui/button";
import { Label } from "./ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { Slider } from "./ui/slider";
import { X, SlidersHorizontal } from "lucide-react";

export const FilterSidebar = ({ filters, selectedFilters, onFilterChange, onClear }) => {
  const [localFilters, setLocalFilters] = useState(selectedFilters);

  useEffect(() => {
    setLocalFilters(selectedFilters);
  }, [selectedFilters]);

  const handleChange = (key, value) => {
    const newFilters = { ...localFilters, [key]: value };
    setLocalFilters(newFilters);
    onFilterChange(newFilters);
  };

  const formatPrice = (price) => {
    return new Intl.NumberFormat('it-IT', {
      style: 'currency',
      currency: 'EUR',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(price);
  };

  const formatMileage = (km) => {
    return new Intl.NumberFormat('it-IT').format(km) + ' km';
  };

  const hasActiveFilters = Object.values(selectedFilters).some(v => v !== null && v !== undefined && v !== '');

  return (
    <div className="bg-white border border-zinc-200 p-6" data-testid="filter-sidebar">
      {/* Header */}
      <div className="flex items-center justify-between mb-6 pb-4 border-b border-zinc-100">
        <div className="flex items-center gap-2">
          <SlidersHorizontal className="w-5 h-5 text-zinc-700" />
          <h3 
            className="text-lg font-bold text-zinc-900 uppercase tracking-wide"
            style={{ fontFamily: 'Barlow Condensed, sans-serif' }}
          >
            Filtri
          </h3>
        </div>
        {hasActiveFilters && (
          <Button
            variant="ghost"
            size="sm"
            onClick={onClear}
            className="text-blue-600 hover:text-blue-700 text-xs uppercase tracking-wide"
            data-testid="clear-filters-btn"
          >
            <X className="w-4 h-4 mr-1" />
            Reset
          </Button>
        )}
      </div>

      <div className="space-y-6">
        {/* Brand */}
        <div>
          <Label className="text-xs font-semibold uppercase tracking-wider text-zinc-500 mb-2 block">
            Marca
          </Label>
          <Select
            value={localFilters.brand || "all"}
            onValueChange={(value) => handleChange('brand', value === "all" ? null : value)}
            data-testid="filter-brand"
          >
            <SelectTrigger className="h-12 bg-white border-zinc-200">
              <SelectValue placeholder="Tutte le marche" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Tutte le marche</SelectItem>
              {filters.brands?.map((brand) => (
                <SelectItem key={brand} value={brand}>
                  {brand}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* Model */}
        <div>
          <Label className="text-xs font-semibold uppercase tracking-wider text-zinc-500 mb-2 block">
            Modello
          </Label>
          <Select
            value={localFilters.model || "all"}
            onValueChange={(value) => handleChange('model', value === "all" ? null : value)}
            data-testid="filter-model"
          >
            <SelectTrigger className="h-12 bg-white border-zinc-200">
              <SelectValue placeholder="Tutti i modelli" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Tutti i modelli</SelectItem>
              {filters.models?.map((model) => (
                <SelectItem key={model} value={model}>
                  {model}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* Fuel Type */}
        <div>
          <Label className="text-xs font-semibold uppercase tracking-wider text-zinc-500 mb-2 block">
            Carburante
          </Label>
          <Select
            value={localFilters.fuel_type || "all"}
            onValueChange={(value) => handleChange('fuel_type', value === "all" ? null : value)}
            data-testid="filter-fuel"
          >
            <SelectTrigger className="h-12 bg-white border-zinc-200">
              <SelectValue placeholder="Tutti i carburanti" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Tutti i carburanti</SelectItem>
              {filters.fuel_types?.map((fuel) => (
                <SelectItem key={fuel} value={fuel}>
                  {fuel.charAt(0).toUpperCase() + fuel.slice(1)}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* Color */}
        <div>
          <Label className="text-xs font-semibold uppercase tracking-wider text-zinc-500 mb-2 block">
            Colore
          </Label>
          <Select
            value={localFilters.color || "all"}
            onValueChange={(value) => handleChange('color', value === "all" ? null : value)}
            data-testid="filter-color"
          >
            <SelectTrigger className="h-12 bg-white border-zinc-200">
              <SelectValue placeholder="Tutti i colori" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Tutti i colori</SelectItem>
              {filters.colors?.map((color) => (
                <SelectItem key={color} value={color}>
                  {color}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* Price Range */}
        {filters.price_range && (
          <div>
            <Label className="text-xs font-semibold uppercase tracking-wider text-zinc-500 mb-3 block">
              Prezzo: {formatPrice(localFilters.price_min || filters.price_range.min)} - {formatPrice(localFilters.price_max || filters.price_range.max)}
            </Label>
            <Slider
              min={filters.price_range.min || 0}
              max={filters.price_range.max || 100000}
              step={500}
              value={[
                localFilters.price_min || filters.price_range.min || 0,
                localFilters.price_max || filters.price_range.max || 100000
              ]}
              onValueChange={([min, max]) => {
                handleChange('price_min', min);
                handleChange('price_max', max);
              }}
              className="py-2"
              data-testid="filter-price-slider"
            />
          </div>
        )}

        {/* Mileage Range */}
        {filters.mileage_range && (
          <div>
            <Label className="text-xs font-semibold uppercase tracking-wider text-zinc-500 mb-3 block">
              Chilometri: {formatMileage(localFilters.mileage_min || filters.mileage_range.min)} - {formatMileage(localFilters.mileage_max || filters.mileage_range.max)}
            </Label>
            <Slider
              min={filters.mileage_range.min || 0}
              max={filters.mileage_range.max || 200000}
              step={5000}
              value={[
                localFilters.mileage_min || filters.mileage_range.min || 0,
                localFilters.mileage_max || filters.mileage_range.max || 200000
              ]}
              onValueChange={([min, max]) => {
                handleChange('mileage_min', min);
                handleChange('mileage_max', max);
              }}
              className="py-2"
              data-testid="filter-mileage-slider"
            />
          </div>
        )}
      </div>
    </div>
  );
};

export default FilterSidebar;
